import { PageHeader } from "@/components/PageHeader";
import { InlineError } from "@/components/QueryState";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  api,
  ApiError,
  type ChannelCostRow,
  type ChannelMode,
  type ChannelRow,
  type ChannelSurfaceType,
  type ChannelUserRow,
  type ThreadFlagRow,
} from "@/lib/api";
import { useSuperAdminView } from "@/lib/use-superadmin-view";
import { useOrgRole } from "@/lib/use-org-role";
import { formatMoney } from "@/lib/format";
import { channelSpendVisible } from "@/lib/show-costs";
import { cn } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useSearch } from "@tanstack/react-router";
import {
  ChevronDown,
  MessageCircleOff,
  MessagesSquare,
  Plus,
  Users,
  UserX,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ChannelDirectoryDialog } from "./ChannelDirectoryDialog";
import { DefaultChannelCard } from "./DefaultChannelCard";
import { ThreadModesSection } from "./ThreadModesSection";
import { useSaveFlagPolicy } from "./use-flag-policy";
import {
  channelUserKey,
  MODES,
  MODE_DOT,
  MODE_HINT,
  MODE_LABEL,
  PLATFORM_LABEL,
  ROW_SURFACE_LABEL,
  rowMode,
} from "./channel-mode";
import { ModePicker } from "./ModePicker";
import { EDIT_REPLY_LABEL, WhenBooAnswers } from "./WhenBooAnswers";

const SURFACE_ORDER: readonly ChannelSurfaceType[] = [
  "dm",
  "group_dm",
  "channel",
  "private_channel",
];

const SURFACE_DEFAULT_LABEL: Record<ChannelSurfaceType, string> = {
  dm: "Direct messages",
  group_dm: "Group chats",
  channel: "Channels",
  private_channel: "Private channels",
};

function formatLastActivity(iso: string | null): string {
  if (!iso) return "—";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

const channelKey = (ch: ChannelRow) => `${ch.platform}:${ch.channelOrSpaceId}`;

// Most recently active first (the conversation you were just in is near the
// top), never-active last, ties broken by name.
function byActivity<T>(last: (t: T) => string | null, name: (t: T) => string) {
  return (a: T, b: T) => {
    const ta = last(a) ? Date.parse(last(a) as string) : -1;
    const tb = last(b) ? Date.parse(last(b) as string) : -1;
    if (tb !== ta) return tb - ta;
    return name(a).localeCompare(name(b));
  };
}

function initialsFor(displayName: string | null, id: string): string {
  if (displayName) {
    const words = displayName.trim().split(/\s+/);
    return words
      .slice(0, 2)
      .map((w) => w[0] ?? "")
      .join("")
      .toUpperCase();
  }
  return id.slice(0, 2).toUpperCase();
}

type PlatformFilter = "all" | ChannelRow["platform"];
// "answering" = replies to at least some inbound (always | mentions);
// "posts_only" and "quiet" are the two non-replying modes, kept separate
// because they behave differently (posts_only can still broadcast; quiet
// declines with the not-available reply).
type StateFilter = "all" | "answering" | "posts_only" | "quiet";

interface NoticeState {
  icon: "channel" | "user";
  text: string;
}

// Floating consequence notice: one slot, replace-never-stack, 12s auto-dismiss.
// Shared by the Channels and People tabs, which each raise their own notices.
function useNotice() {
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const noticeTimer = useRef<number | null>(null);
  const showNotice = (next: NoticeState) => {
    if (noticeTimer.current !== null) window.clearTimeout(noticeTimer.current);
    setNotice(next);
    noticeTimer.current = window.setTimeout(() => setNotice(null), 12000);
  };
  useEffect(
    () => () => {
      if (noticeTimer.current !== null) window.clearTimeout(noticeTimer.current);
    },
    [],
  );
  return { notice, setNotice, showNotice };
}

export function ChannelsTab() {
  const params = useParams({ strict: false }) as { orgId?: string; containerId?: string };
  const containerId = params.containerId!;
  const orgId = params.orgId!;
  const queryClient = useQueryClient();
  // The chief-of-staff seed leaves DMs closed to new people and channels
  // quiet; say so once here, where the operator changes it.
  const containersQuery = useQuery({ queryKey: ["containers"], queryFn: api.containers.list });
  const thisContainer = containersQuery.data?.data.find((c) => c.id === containerId);
  const isChiefOfStaff = thisContainer?.posture === "chief-of-staff";

  const [search, setSearch] = useState("");
  const [platform, setPlatform] = useState<PlatformFilter>("all");
  const [stateFilter, setStateFilter] = useState<StateFilter>("all");
  const [replyOpen, setReplyOpen] = useState(false);
  const { notice, setNotice, showNotice } = useNotice();

  const queryKey = ["channels", containerId];
  const query = useQuery({
    queryKey,
    queryFn: () => api.containers.channels.list(containerId),
    enabled: !!containerId,
  });

  // surfaceDefaults doubles as the feature flag: absent means this Boo
  // predates channel-management v2, so the tab falls back to the boolean
  // responding contract (single default switch, on/off per row, no people,
  // no platform report or directory pickers, no reply bar).
  const hasModes = !!query.data?.surfaceDefaults;
  const blockNewUsers = query.data?.blockNewUsers ?? false;

  const platformsQuery = useQuery({
    queryKey: ["channel-platforms", containerId],
    queryFn: () => api.containers.channels.platforms(containerId),
    enabled: !!containerId && hasModes,
  });
  const platformStatuses = platformsQuery.data?.platforms;
  // Unavailable platforms are hidden everywhere — a platform this Boo doesn't
  // have isn't worth screen space. The dashboard surface is always there and
  // would only add noise, so it's not offered for adding either.
  const chatPlatformStatuses = platformStatuses?.filter((p) => p.platform !== "dashboard_chat");
  const addablePlatforms = (chatPlatformStatuses ?? []).filter((p) => p.enabled);

  // Channel-wise spend is visible to org owners/admins when the
  // `channel-wise-spend` flag is enabled for their org, and to super admins
  // with staff extras on and `?costs=true` in the URL.
  // The backend is the source of truth — it 403s callers who don't qualify —
  // so we enable the query for anyone who *might* qualify (super admin or an
  // org manager) and render the column only once real data comes back.
  // (Customers also get their costs on the org Usage page.)
  const isSuperAdmin = useSuperAdminView();
  const { costs: costsParam } = useSearch({ strict: false }) as { costs?: unknown };
  const showCosts = costsParam === true;
  const { canManage } = useOrgRole();
  const costs = useQuery({
    queryKey: ["channel-costs", containerId],
    queryFn: () => api.containers.costsByChannel(containerId),
    enabled: !!containerId && (isSuperAdmin || canManage),
    retry: false,
  });
  // A 403 (org without the flag, or a plain member) leaves `costs.data`
  // undefined, so the Spend column simply doesn't render. A super admin always
  // gets data, so without the dock on and `costs=true` they see the column
  // only where the customer would (`customerVisible`).
  const showSpendColumn =
    !!costs.data &&
    channelSpendVisible({ customerVisible: costs.data.customerVisible, superAdminView: isSuperAdmin, costs: showCosts });
  // Join contract with the channel list: cost rows key on
  // `chatPlatform:channelId`, channel rows on `platform:channelOrSpaceId` —
  // the same BooThreadKey vocabulary (BOO-320). The backend normalizes both
  // sides to the container-level channel id (e.g. gchat thread keys collapse
  // to the space id), so a plain string match is sufficient here.
  const costByChannel = useMemo(() => {
    const map = new Map<string, ChannelCostRow>();
    for (const row of costs.data?.channels ?? []) {
      map.set(`${row.chatPlatform}:${row.channelId}`, row);
    }
    return map;
  }, [costs.data]);

  const canToggle = query.data?._permissions?.canToggle !== false;
  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  // Thread-flag policy (approver allowlists + the Extras thread-modes surface)
  // now lives in PeopleTab and ExtrasTab respectively — this tab is channels
  // only.

  const singleQuietText = (name: string): string =>
    `${name} now stays quiet. If someone @-mentions Boo there, it sends its not-available reply.`;
  const bulkQuietText = (count: number): string =>
    `${count} channels now stay quiet. If someone @-mentions Boo there, it sends its not-available reply.`;

  const setRowMode = useMutation({
    mutationFn: (vars: { channel: ChannelRow; mode: ChannelMode }) =>
      hasModes
        ? api.containers.channels.setMode(containerId, vars.channel, vars.mode)
        : api.containers.channels.setResponding(
            containerId,
            vars.channel,
            vars.mode !== "never",
          ),
    onSuccess: (_data, vars) => {
      invalidate();
      if (hasModes && vars.mode === "never") {
        showNotice({
          icon: "channel",
          text: singleQuietText(vars.channel.displayName ?? vars.channel.channelOrSpaceId),
        });
      }
    },
  });
  const setSurfaceDefault = useMutation({
    mutationFn: (vars: { surfaceType: ChannelSurfaceType; mode: ChannelMode }) =>
      api.containers.channels.setSurfaceDefault(containerId, vars.surfaceType, vars.mode),
    onSuccess: invalidate,
  });
  // Legacy single default switch (pre-mode Boos only).
  const setDefault = useMutation({
    mutationFn: (responding: boolean) =>
      api.containers.channels.setDefault(containerId, responding),
    onSuccess: invalidate,
  });
  const bulk = useMutation({
    mutationFn: async (vars: { channels: ChannelRow[]; mode: ChannelMode }) => {
      for (const ch of vars.channels) {
        if (hasModes) {
          await api.containers.channels.setMode(containerId, ch, vars.mode);
        } else {
          await api.containers.channels.setResponding(containerId, ch, vars.mode !== "never");
        }
      }
    },
    onSuccess: (_data, vars) => {
      invalidate();
      if (hasModes && vars.mode === "never") {
        showNotice({ icon: "channel", text: bulkQuietText(vars.channels.length) });
      }
    },
  });
  // Channel-scoped person blocks: click a channel row → sheet of people with
  // per-this-channel toggles. Gated on the brain's capability flag — an older
  // brain would silently apply the toggle as an everywhere-block.
  const userChannelBlocks = query.data?.userChannelBlocks === true;
  // Key, not row: the open sheet must re-render from the fresh query row after
  // a toggle invalidates, or its allowlist switch would show stale state.
  const [peopleChannelKey, setPeopleChannelKey] = useState<string | null>(null);
  const setAllowlistOnly = useMutation({
    mutationFn: (vars: { channel: ChannelRow; allowlistOnly: boolean }) =>
      api.containers.channels.setChannelAllowlistOnly(
        containerId,
        vars.channel,
        vars.allowlistOnly,
      ),
    onSuccess: (_data, vars) => {
      invalidate();
      if (vars.allowlistOnly) {
        const channelName = vars.channel.displayName ?? vars.channel.channelOrSpaceId;
        showNotice({
          icon: "channel",
          text: `Boo now answers only allowed people in ${channelName}. Switch people on in the channel's list.`,
        });
      }
    },
  });
  const setUserChannelBlocked = useMutation({
    mutationFn: (vars: { user: ChannelUserRow; channel: ChannelRow; blocked: boolean }) =>
      api.containers.channels.setUserChannelBlocked(
        containerId,
        vars.user,
        vars.channel.channelOrSpaceId,
        vars.blocked,
      ),
    onSuccess: (_data, vars) => {
      invalidate();
      const name = vars.user.displayName ?? vars.user.providerUserId;
      const channelName = vars.channel.displayName ?? vars.channel.channelOrSpaceId;
      showNotice({
        icon: "user",
        text: vars.blocked
          ? `Boo stays quiet to ${name} in ${channelName}. It still answers them everywhere else.`
          : `Boo answers ${name} in ${channelName} again.`,
      });
    },
  });

  const filterPlatforms: readonly ChannelRow["platform"][] = chatPlatformStatuses
    ? chatPlatformStatuses.filter((p) => p.enabled).map((p) => p.platform)
    : ["slack", "gchat", "teams"];
  // A platform filter with one real option is noise — drop it once the
  // platform report confirms there's nothing to switch between. Falls back to
  // "all" so a stale selection can't silently hide rows.
  const platformFilterVisible = !chatPlatformStatuses || filterPlatforms.length > 1;
  const activePlatform = platformFilterVisible ? platform : "all";

  const channels = useMemo(() => query.data?.channels ?? [], [query.data]);
  const filtersActive =
    search.trim().length > 0 || activePlatform !== "all" || stateFilter !== "all";
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return channels
      .filter((ch) => {
        if (activePlatform !== "all" && ch.platform !== activePlatform) return false;
        const mode = rowMode(ch);
        if (stateFilter === "answering" && mode !== "always" && mode !== "mentions")
          return false;
        if (stateFilter === "posts_only" && mode !== "posts_only") return false;
        if (stateFilter === "quiet" && mode !== "never") return false;
        if (q && !`${ch.displayName ?? ""} ${ch.channelOrSpaceId}`.toLowerCase().includes(q)) {
          return false;
        }
        return true;
      })
      .sort(
        byActivity(
          (ch) => ch.lastMessageAt,
          (ch) => ch.displayName ?? ch.channelOrSpaceId,
        ),
      );
  }, [channels, search, activePlatform, stateFilter]);

  const users = query.data?.users ?? [];
  const peopleChannel =
    channels.find((ch) => channelKey(ch) === peopleChannelKey) ?? null;
  // Same "only show what varies" rule everywhere: a Platform column/segment
  // where every row says the same thing carries no information.
  const showPlatformColumn = new Set(channels.map((ch) => ch.platform)).size > 1;
  const busy =
    setRowMode.isPending ||
    setSurfaceDefault.isPending ||
    setDefault.isPending ||
    bulk.isPending;
  const mutationError =
    setRowMode.error ??
    setSurfaceDefault.error ??
    setDefault.error ??
    bulk.error ??
    setUserChannelBlocked.error ??
    setAllowlistOnly.error;

  // Surfaces worth a default: produced by at least one platform this Boo has
  // (all platforms while the platform report is still loading). The full
  // platform × surface grid would show options that can never occur.
  const platformSurfaces = query.data?.platformSurfaces;
  const surfacePlatformLabels = useMemo(() => {
    const map = new Map<ChannelSurfaceType, string>();
    if (!platformSurfaces) return map;
    const enabledSet = platformStatuses
      ? new Set(platformStatuses.filter((p) => p.enabled).map((p) => p.platform))
      : undefined;
    for (const surface of SURFACE_ORDER) {
      const platforms = (
        Object.entries(platformSurfaces) as [ChannelRow["platform"], ChannelSurfaceType[]][]
      )
        .filter(([p]) => enabledSet?.has(p) !== false)
        .filter(([, surfaces]) => surfaces.includes(surface))
        .map(([p]) => PLATFORM_LABEL[p]);
      if (platforms.length > 0) map.set(surface, platforms.join(" · "));
    }
    return map;
  }, [platformSurfaces, platformStatuses]);
  const renderedSurfaces = SURFACE_ORDER.filter((s) => surfacePlatformLabels.has(s));

  const enabledChatKnown = chatPlatformStatuses !== undefined;
  const anyChatEnabled = addablePlatforms.length > 0;

  const addChannelsDialog =
    canToggle && anyChatEnabled ? (
      <ChannelDirectoryDialog
        containerId={containerId}
        kind="channel"
        platforms={addablePlatforms}
        blockNewUsers={blockNewUsers}
        trigger={
          <Button size="sm">
            <Plus className="size-4" />
            Add channels
          </Button>
        }
      />
    ) : null;

  const clearFilters = () => {
    setSearch("");
    setPlatform("all");
    setStateFilter("all");
  };

  // Absent key = the Boo predates the default channel; null = none set.
  const defaultChannel = query.data?.defaultChannel;
  const defaultChannelKey = defaultChannel
    ? `${defaultChannel.platform}:${defaultChannel.channelOrSpaceId}`
    : null;

  const channelsSection = (
    <>
      {query.data && defaultChannel !== undefined && (
        <DefaultChannelCard
          containerId={containerId}
          data={query.data}
          platformStatuses={platformStatuses}
          canToggle={canToggle}
          botName={thisContainer?.name}
        />
      )}
      {hasModes ? (
        <NewConversationsDefaults
          surfaces={renderedSurfaces}
          surfaceDefaults={query.data?.surfaceDefaults}
          platformLabels={surfacePlatformLabels}
          showPlatformSub={addablePlatforms.length > 1}
          canToggle={canToggle}
          busy={busy}
          onChange={(surfaceType, mode) => setSurfaceDefault.mutate({ surfaceType, mode })}
        />
      ) : (
        <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-4">
          <div>
            <div className="text-sm font-medium">Respond in new channels by default</div>
            <p className="text-sm text-muted-foreground max-w-prose">
              Sets the starting state for channels Boo is added to next. Doesn&apos;t change
              the ones already listed below.
            </p>
          </div>
          <Switch
            checked={query.data?.respondByDefault ?? true}
            onCheckedChange={(checked) => setDefault.mutate(checked)}
            disabled={!canToggle || setDefault.isPending}
          />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search channels…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 w-56"
        />
        {platformFilterVisible && (
          <FilterGroup<PlatformFilter>
            value={platform}
            onChange={setPlatform}
            options={[
              { value: "all", label: "All" },
              ...filterPlatforms.map((p) => ({ value: p, label: PLATFORM_LABEL[p] })),
            ]}
          />
        )}
        <FilterGroup<StateFilter>
          value={stateFilter}
          onChange={setStateFilter}
          options={[
            { value: "all", label: "All" },
            { value: "answering", label: "Answering" },
            // Posts-only is a v2-mode concept; a pre-mode Boo can't be in it,
            // so the filter chip only earns its place when modes are on.
            ...(hasModes
              ? [{ value: "posts_only" as const, label: "Posts only" }]
              : []),
            { value: "quiet", label: "Quiet" },
          ]}
        />
        {canToggle && (
          <div className="ml-auto flex gap-2">
            {hasModes ? (
              // Deliberate: selecting a mode applies immediately with no
              // confirm step — the live count in the menu label is the guard.
              <DropdownMenu>
                <DropdownMenuTrigger asChild disabled={busy || filtered.length === 0}>
                  <Button variant="outline" size="sm">
                    Change all…
                    <ChevronDown className="size-4 opacity-50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                    {filtered.length === 1
                      ? "Applies to the 1 channel shown"
                      : `Applies to the ${filtered.length} channels shown`}
                  </DropdownMenuLabel>
                  {MODES.map((mode) => (
                    <DropdownMenuItem
                      key={mode}
                      className="flex-col items-start gap-0.5"
                      onSelect={() => bulk.mutate({ channels: filtered, mode })}
                    >
                      <span className="flex items-center gap-2">
                        <span className={cn("size-2 shrink-0 rounded-full", MODE_DOT[mode])} />
                        {MODE_LABEL[mode]}
                      </span>
                      <span className="text-xs text-muted-foreground">{MODE_HINT[mode]}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy || filtered.length === 0}
                  onClick={() => bulk.mutate({ channels: filtered, mode: "mentions" })}
                >
                  Answer in all
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy || filtered.length === 0}
                  onClick={() => bulk.mutate({ channels: filtered, mode: "never" })}
                >
                  Quiet in all
                </Button>
              </>
            )}
            {addChannelsDialog}
          </div>
        )}
      </div>

      <div className="text-sm text-muted-foreground tabular-nums">
        {filtersActive
          ? `${filtered.length} of ${channels.length} channels`
          : `${channels.length} ${channels.length === 1 ? "channel" : "channels"}`}
      </div>

      {channels.length === 0 ? (
        enabledChatKnown && !anyChatEnabled && hasModes ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 py-10 text-center">
            <MessagesSquare className="size-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground max-w-prose">
              Connect Slack, Google Chat, or Teams to give Boo somewhere to listen.
            </p>
            <Button variant="outline" size="sm" asChild>
              <Link
                to="/orgs/$orgId/containers/$containerId/integrations"
                params={{ orgId, containerId }}
              >
                Go to Integrations
              </Link>
            </Button>
          </div>
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 py-10 text-center">
            <MessagesSquare className="size-8 text-muted-foreground" />
            <div className="text-sm font-medium">No channels yet</div>
            <p className="text-sm text-muted-foreground max-w-prose">
              {addChannelsDialog
                ? "Boo adds a channel here the first time someone writes where it's listening — or add some ahead of time and choose how Boo should behave in each."
                : "Boo adds a channel here the first time someone writes where it's listening."}
            </p>
            {addChannelsDialog}
          </div>
        )
      ) : filtered.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 py-10 text-center">
          <p className="text-sm text-muted-foreground">No channels match.</p>
          <Button variant="link" size="sm" onClick={clearFilters}>
            Clear filters
          </Button>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Last active</TableHead>
              {showSpendColumn && (
                <TableHead className="text-right">Spend (this month)</TableHead>
              )}
              <TableHead className="text-right">
                {hasModes ? "What Boo does" : "Answering"}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((ch) => {
              const mode = rowMode(ch);
              const stateNote =
                mode === "never"
                  ? "Reading along, not replying"
                  : mode === "posts_only"
                    ? "Posts only, never replies"
                    : null;
              const subLine = [
                ...(showPlatformColumn ? [PLATFORM_LABEL[ch.platform]] : []),
                ROW_SURFACE_LABEL[ch.surfaceType] ?? ch.surfaceType,
                ...(stateNote ? [stateNote] : []),
              ].join(" · ");
              // A DM is one person — per-channel blocking there is just the
              // People tab's "Gets answers" switch, so no sheet for DMs.
              const canOpenPeople = userChannelBlocks && ch.surfaceType !== "dm";
              return (
                <TableRow
                  key={channelKey(ch)}
                  className={cn("hover:bg-muted/50", canOpenPeople && "cursor-pointer")}
                  onClick={canOpenPeople ? () => setPeopleChannelKey(channelKey(ch)) : undefined}
                >
                  <TableCell className="font-medium">
                    <div className="flex flex-col gap-0.5">
                      <span className="flex items-center gap-2">
                        {ch.displayName ?? (
                          <span className="font-mono text-xs text-muted-foreground">
                            {ch.channelOrSpaceId}
                          </span>
                        )}
                        {channelKey(ch) === defaultChannelKey && (
                          <Badge variant="secondary">Default</Badge>
                        )}
                        {ch.presence === "removed" && (
                          <Badge variant="outline" className="text-muted-foreground">
                            Removed
                          </Badge>
                        )}
                      </span>
                      <span className="text-xs text-muted-foreground">{subLine}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground tabular-nums">
                    {formatLastActivity(ch.lastMessageAt)}
                  </TableCell>
                  {showSpendColumn && (
                    <TableCell className="text-right tabular-nums">
                      <ChannelSpend
                        row={costByChannel.get(channelKey(ch))}
                        currency={costs.data?.currency ?? "USD"}
                      />
                    </TableCell>
                  )}
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    {hasModes ? (
                      <ModePicker
                        value={rowMode(ch)}
                        onChange={(mode) => setRowMode.mutate({ channel: ch, mode })}
                        disabled={!canToggle || busy || ch.presence === "removed"}
                      />
                    ) : (
                      <Switch
                        checked={ch.responding}
                        onCheckedChange={(checked) =>
                          setRowMode.mutate({ channel: ch, mode: checked ? "mentions" : "never" })
                        }
                        disabled={!canToggle || busy || ch.presence === "removed"}
                      />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </>
  );

  return (
    <div className="flex flex-1 flex-col min-h-0 overflow-auto p-6 gap-5">
      <PageHeader
        title="Channels"
        subtitle="Who Boo answers, where it listens, and what it says when it can't take part."
      />

      {isChiefOfStaff && (
        <p className="-mt-2 text-sm text-muted-foreground">
          Chief of staff: only you can DM Boo and channels stay quiet until you
          change it here.
        </p>
      )}

      {!canToggle && !query.isLoading && (
        <p className="-mt-2 text-sm text-muted-foreground">
          You can look around, but only owners and admins can change where Boo responds.
        </p>
      )}

      {query.isLoading ? (
        <PageSkeleton />
      ) : query.isError ? (
        <InlineError error={query.error} />
      ) : (
        // People now lives in its own top-level tab (PeopleTab); this tab is
        // channels only. When Boo answers (shared with People) leads; the
        // section renders the legacy boolean flow when !hasModes.
        // mt-3 on the page's gap-5 puts the block 32px under the header, as on People.
        <div className="mt-3 flex flex-col gap-8">
          <WhenBooAnswers containerId={containerId} replyOpen={replyOpen} onReplyOpenChange={setReplyOpen} />
          <div className="flex flex-col gap-4">{channelsSection}</div>
        </div>
      )}

      <ChannelPeopleSheet
        channel={peopleChannel}
        users={users}
        canToggle={canToggle}
        busy={setUserChannelBlocked.isPending || setAllowlistOnly.isPending}
        onToggle={(user, channel, blocked) =>
          setUserChannelBlocked.mutate({ user, channel, blocked })
        }
        onAllowlistOnly={(channel, allowlistOnly) =>
          setAllowlistOnly.mutate({ channel, allowlistOnly })
        }
        onOpenChange={(open) => {
          if (!open) setPeopleChannelKey(null);
        }}
      />

      {notice && (
        <FloatingNotice
          notice={notice}
          canToggle={canToggle}
          onChangeReply={() => {
            setNotice(null);
            setReplyOpen(true);
          }}
          onDismiss={() => setNotice(null)}
        />
      )}

      {mutationError != null && <InlineError error={mutationError} />}
    </div>
  );
}

// People on a Boo from before the current People screen. Shares the channels
// query (React Query dedupes the key) and leads with the same When Boo
// answers block as Channels.
export function PeopleTab() {
  const params = useParams({ strict: false }) as { orgId?: string; containerId?: string };
  const containerId = params.containerId!;
  const queryClient = useQueryClient();

  const [replyOpen, setReplyOpen] = useState(false);
  const { notice, setNotice, showNotice } = useNotice();

  const queryKey = ["channels", containerId];
  const query = useQuery({
    queryKey,
    queryFn: () => api.containers.channels.list(containerId),
    enabled: !!containerId,
  });

  const hasModes = !!query.data?.surfaceDefaults;
  const blockNewUsers = query.data?.blockNewUsers ?? false;
  const canToggle = query.data?._permissions?.canToggle !== false;
  const canManageSpend = query.data?._permissions?.canManageSpendAccess === true;
  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  const users = query.data?.users ?? [];

  // Add-a-person needs the same enabled-platform list the Channels tab uses.
  const platformsQuery = useQuery({
    queryKey: ["channel-platforms", containerId],
    queryFn: () => api.containers.channels.platforms(containerId),
    enabled: !!containerId && hasModes,
  });
  const chatPlatformStatuses = platformsQuery.data?.platforms?.filter(
    (p) => p.platform !== "dashboard_chat",
  );
  const addablePlatforms = (chatPlatformStatuses ?? []).filter((p) => p.enabled);
  const anyChatEnabled = addablePlatforms.length > 0;

  const setUserBlocked = useMutation({
    mutationFn: (vars: { user: ChannelUserRow; blocked: boolean }) =>
      api.containers.channels.setUserBlocked(containerId, vars.user, vars.blocked),
    onSuccess: (_data, vars) => {
      invalidate();
      if (vars.blocked) {
        const name = vars.user.displayName ?? vars.user.providerUserId;
        showNotice({
          icon: "user",
          text: `${name} is blocked. If they write to Boo, it sends its not-available reply.`,
        });
      }
    },
  });
  const setUserSpendAccess = useMutation({
    mutationFn: (vars: { user: ChannelUserRow; viewAllSpend: boolean }) =>
      api.containers.channels.setUserSpendAccess(
        containerId,
        vars.user,
        vars.viewAllSpend,
        vars.user.displayName ?? undefined,
      ),
    onSuccess: (_data, vars) => {
      invalidate();
      const name = vars.user.displayName ?? vars.user.providerUserId;
      showNotice({
        icon: "user",
        text: vars.viewAllSpend
          ? `${name} can now see all spend.`
          : `${name} can no longer see all spend.`,
      });
    },
  });
  // Approver allowlists mirror the Extras thread-flag policy: the People table
  // shows an "Approves …" switch per approval flag. Same query the Extras tab
  // uses (React Query dedupes the key); admin + v2 only. A 409/403 means the
  // Boo predates the flags routes — don't retry, just render no approver column.
  const flagsQuery = useQuery({
    queryKey: ["thread-flags", containerId],
    queryFn: () => api.containers.flags.list(containerId),
    enabled: !!containerId && hasModes && canToggle,
    retry: (failureCount, error) =>
      !(error instanceof ApiError && (error.status === 409 || error.status === 403)) &&
      failureCount < 2,
  });
  const threadFlags = flagsQuery.data?.flags ?? [];
  const tableFlagSave = useSaveFlagPolicy(containerId);
  const approvalFlags = threadFlags.filter(
    (f) => f.approvable !== false && f.access === "approval" && Array.isArray(f.approvers),
  );
  const toggleApprover = (flag: ThreadFlagRow, user: ChannelUserRow, next: boolean) => {
    const key = channelUserKey(user);
    const current = flag.approvers as string[];
    tableFlagSave.mutate({
      flagId: flag.id,
      access: flag.access,
      approvers: next ? [...current, key] : current.filter((k) => k !== key),
    });
  };

  const busy =
    setUserBlocked.isPending ||
    setUserSpendAccess.isPending ||
    tableFlagSave.isPending;
  const mutationError =
    setUserBlocked.error ??
    setUserSpendAccess.error ??
    tableFlagSave.error;

  return (
    <div className="flex flex-1 flex-col min-h-0 overflow-auto p-6 gap-5">
      <PageHeader
        title="People"
        subtitle="Who Boo answers in direct messages, and who it holds back from."
      />

      {!canToggle && !query.isLoading && (
        <p className="-mt-2 text-sm text-muted-foreground">
          You can look around, but only owners and admins can change who Boo answers.
        </p>
      )}

      {query.isLoading ? (
        <PageSkeleton />
      ) : query.isError ? (
        <InlineError error={query.error} />
      ) : (
        <div className="mt-3 flex flex-col gap-8">
          <WhenBooAnswers containerId={containerId} replyOpen={replyOpen} onReplyOpenChange={setReplyOpen} />
          <div className="flex flex-col gap-4">
          <PeopleSection
            users={users}
            canToggle={canToggle}
            canManageSpend={canManageSpend}
            busy={busy}
            spendBusy={setUserSpendAccess.isPending}
            onUserBlocked={(user, blocked) => setUserBlocked.mutate({ user, blocked })}
            onUserSpendAccess={(user, viewAllSpend) =>
              setUserSpendAccess.mutate({ user, viewAllSpend })
            }
            approvalFlags={approvalFlags}
            flagsBusy={tableFlagSave.isPending}
            onToggleApprover={toggleApprover}
            addDialog={
              canToggle && anyChatEnabled ? (
                <ChannelDirectoryDialog
                  containerId={containerId}
                  kind="user"
                  platforms={addablePlatforms}
                  defaultBlocked={!blockNewUsers}
                  blockNewUsers={blockNewUsers}
                  trigger={
                    <Button size="sm">
                      <Plus className="size-4" />
                      Add a person
                    </Button>
                  }
                />
              ) : null
            }
          />
          </div>
        </div>
      )}

      {notice && (
        <FloatingNotice
          notice={notice}
          canToggle={canToggle}
          onChangeReply={() => {
            setNotice(null);
            setReplyOpen(true);
          }}
          onDismiss={() => setNotice(null)}
        />
      )}

      {mutationError != null && <InlineError error={mutationError} />}
    </div>
  );
}

// Extras — its own top-level tab (was a sub-tab inside Channels). Thread-flag
// policy: the optional per-conversation behaviors a Boo can turn on. The
// container tab strip only shows Extras when there are flags to manage, so
// reaching here generally means threadFlags is non-empty.
export function ExtrasTab() {
  const params = useParams({ strict: false }) as { orgId?: string; containerId?: string };
  const containerId = params.containerId!;

  // Reuse the channels query (deduped) for permissions + the people roster the
  // approver cards reference.
  const channelsQuery = useQuery({
    queryKey: ["channels", containerId],
    queryFn: () => api.containers.channels.list(containerId),
    enabled: !!containerId,
  });
  const canToggle = channelsQuery.data?._permissions?.canToggle !== false;
  const hasModes = !!channelsQuery.data?.surfaceDefaults;
  const users = channelsQuery.data?.users ?? [];

  const flagsQuery = useQuery({
    queryKey: ["thread-flags", containerId],
    queryFn: () => api.containers.flags.list(containerId),
    enabled: !!containerId && hasModes && canToggle,
    retry: (failureCount, error) =>
      !(error instanceof ApiError && (error.status === 409 || error.status === 403)) &&
      failureCount < 2,
  });
  const threadFlags = flagsQuery.data?.flags ?? [];

  return (
    <div className="flex flex-1 flex-col min-h-0 overflow-auto p-6 gap-5">
      <PageHeader
        title="Extras"
        subtitle="Optional behaviors Boo can turn on — for a single conversation, or for the whole Boo."
      />
      {flagsQuery.isLoading ? (
        <PageSkeleton />
      ) : flagsQuery.isError ? (
        <div className="flex flex-col items-start gap-2">
          <p className="text-sm text-muted-foreground">
            Couldn&apos;t load conversation extras.
          </p>
          <Button
            variant="outline"
            size="sm"
            disabled={flagsQuery.isFetching}
            onClick={() => flagsQuery.refetch()}
          >
            Try again
          </Button>
        </div>
      ) : (
        <ThreadModesSection
          containerId={containerId}
          flags={threadFlags}
          users={users}
          canToggle={canToggle}
        />
      )}
    </div>
  );
}

// Fixed-overlay consequence notice: never reflows the table, always in
// viewport, quotes the actual current reply at the moment it starts applying.
function FloatingNotice({
  notice,
  canToggle,
  onChangeReply,
  onDismiss,
}: {
  notice: NoticeState;
  canToggle: boolean;
  onChangeReply: () => void;
  onDismiss: () => void;
}) {
  const Icon = notice.icon === "user" ? UserX : MessageCircleOff;
  return (
    <div className="fixed bottom-6 right-6 z-50 w-[min(24rem,calc(100vw-3rem))] animate-in fade-in slide-in-from-bottom-2">
      <Alert className="relative bg-background pr-10 shadow-lg">
        <Icon className="size-4" />
        <AlertDescription className="flex flex-col items-start gap-1">
          <span>{notice.text}</span>
          {canToggle && (
            <Button
              variant="link"
              size="sm"
              className="h-auto px-0 py-1"
              onClick={onChangeReply}
            >
              {EDIT_REPLY_LABEL}
            </Button>
          )}
        </AlertDescription>
        <button
          aria-label="Dismiss"
          onClick={onDismiss}
          className="absolute right-1 top-1 rounded-md p-2.5 text-muted-foreground hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </Alert>
    </div>
  );
}

// Seed defaults — the single most consequential control on the page, so it
// leads and stays open (a prior version collapsed it to one line, which buried
// the thing people most often come here to set). One row per surface, each
// with its own mode.
function NewConversationsDefaults({
  surfaces,
  surfaceDefaults,
  platformLabels,
  showPlatformSub,
  canToggle,
  busy,
  onChange,
}: {
  surfaces: readonly ChannelSurfaceType[];
  surfaceDefaults: Partial<Record<ChannelSurfaceType, ChannelMode>> | undefined;
  platformLabels: Map<ChannelSurfaceType, string>;
  showPlatformSub: boolean;
  canToggle: boolean;
  busy: boolean;
  onChange: (surfaceType: ChannelSurfaceType, mode: ChannelMode) => void;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-muted/30 p-4">
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium">New conversations start as</span>
        <p className="text-sm text-muted-foreground max-w-prose">
          How Boo starts out in each place it&apos;s added to next. Changing this
          never touches the channels already listed below.
        </p>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {surfaces.map((surface) => (
          <div
            key={surface}
            className="flex items-center justify-between gap-3 rounded-md border border-border/60 bg-background px-3 py-2"
          >
            <div className="flex flex-col">
              <span className="text-sm">{SURFACE_DEFAULT_LABEL[surface]}</span>
              {showPlatformSub && (
                <span className="text-xs text-muted-foreground">
                  {platformLabels.get(surface)}
                </span>
              )}
            </div>
            <ModePicker
              value={surfaceDefaults?.[surface] ?? "mentions"}
              onChange={(mode) => onChange(surface, mode)}
              disabled={!canToggle || busy}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// People management: everyone who has messaged Boo, individually blockable,
// plus the allow-first flag for future senders. When an extra's approval is
// limited to specific people, its allowlist mirrors here as an "Approves …"
// switch column plus an Approvers filter — a secondary surface for scanning
// and bulk granting; the Extras card stays the owning surface.
function PeopleSection({
  users,
  canToggle,
  canManageSpend,
  busy,
  spendBusy,
  onUserBlocked,
  onUserSpendAccess,
  approvalFlags,
  flagsBusy,
  onToggleApprover,
  addDialog,
}: {
  users: ChannelUserRow[];
  canToggle: boolean;
  canManageSpend: boolean;
  busy: boolean;
  spendBusy: boolean;
  onUserBlocked: (user: ChannelUserRow, blocked: boolean) => void;
  onUserSpendAccess: (user: ChannelUserRow, viewAllSpend: boolean) => void;
  approvalFlags: ThreadFlagRow[];
  flagsBusy: boolean;
  onToggleApprover: (flag: ThreadFlagRow, user: ChannelUserRow, next: boolean) => void;
  addDialog?: ReactNode;
}) {
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<"all" | "approvers">("all");
  const showPlatformColumn = new Set(users.map((u) => u.platform)).size > 1;
  // A stale "Approvers" selection with no approval flags left would silently
  // hide everyone — fall back to "all" whenever the filter isn't rendered.
  const activeRoleFilter = approvalFlags.length > 0 ? roleFilter : "all";
  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users
      .filter((u) => {
        if (
          activeRoleFilter === "approvers" &&
          !approvalFlags.some((f) => (f.approvers as string[]).includes(channelUserKey(u)))
        ) {
          return false;
        }
        return !q || `${u.displayName ?? ""} ${u.providerUserId}`.toLowerCase().includes(q);
      })
      .sort(
        byActivity(
          (u) => u.lastMessageAt,
          (u) => u.displayName ?? u.providerUserId,
        ),
      );
  }, [users, search, activeRoleFilter, approvalFlags]);
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search people…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 w-56"
        />
        {approvalFlags.length > 0 && (
          <FilterGroup<"all" | "approvers">
            value={activeRoleFilter}
            onChange={setRoleFilter}
            options={[
              { value: "all", label: "All" },
              { value: "approvers", label: "Approvers" },
            ]}
          />
        )}
        <div className="ml-auto">{addDialog}</div>
      </div>

      <div className="text-sm text-muted-foreground tabular-nums">
        {search.trim() || activeRoleFilter !== "all"
          ? `${matches.length} of ${users.length} people`
          : `${users.length} ${users.length === 1 ? "person" : "people"}`}
      </div>

      {users.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 py-10 text-center">
          <Users className="size-8 text-muted-foreground" />
          <div className="text-sm font-medium">No one yet</div>
          <p className="text-sm text-muted-foreground max-w-prose">
            People show up here the first time they write to Boo. You can also add someone
            early — to welcome them in, or to keep Boo from answering them.
          </p>
          {addDialog}
        </div>
      ) : matches.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 py-10 text-center">
          <p className="text-sm text-muted-foreground">No one matches.</p>
          <Button
            variant="link"
            size="sm"
            onClick={() => {
              setSearch("");
              setRoleFilter("all");
            }}
          >
            Clear filters
          </Button>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Last active</TableHead>
              {/* Always named with its extra — a bare "Can approve" next to
                  "Gets answers" has no object, and the Extras card copy must
                  be able to reference a stable label. */}
              {approvalFlags.map((flag) => (
                <TableHead key={flag.id} className="text-right">
                  Approves {flag.title}
                </TableHead>
              ))}
              {canManageSpend && <TableHead className="text-right">Permissions</TableHead>}
              <TableHead className="text-right">Gets answers</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {matches.map((user) => {
              const quietCount = Object.values(user.channelOverrides ?? {}).filter(
                (v) => v === "block",
              ).length;
              const subLine = [
                ...(showPlatformColumn ? [PLATFORM_LABEL[user.platform]] : []),
                ...(user.blocked
                  ? ["Blocked", "gets Boo's not-available reply"]
                  : quietCount > 0
                    ? [`Quiet in ${quietCount} ${quietCount === 1 ? "channel" : "channels"}`]
                    : []),
              ].join(" · ");
              return (
                <TableRow key={`${user.platform}:${user.providerUserId}`} className="hover:bg-muted/50">
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2.5">
                      <Avatar className="size-7 shrink-0">
                        <AvatarFallback className="text-[10px]">
                          {initialsFor(user.displayName, user.providerUserId)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex min-w-0 flex-col gap-0.5">
                        <span className={cn("truncate", user.blocked && "text-muted-foreground")}>
                          {user.displayName ?? (
                            <span className="font-mono text-xs text-muted-foreground">
                              {user.providerUserId}
                            </span>
                          )}
                        </span>
                        {subLine && (
                          <span className="truncate text-xs text-muted-foreground">{subLine}</span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground tabular-nums">
                    {formatLastActivity(user.lastMessageAt)}
                  </TableCell>
                  {approvalFlags.map((flag) => (
                    <TableCell key={flag.id} className="text-right">
                      <Switch
                        checked={(flag.approvers as string[]).includes(
                          channelUserKey(user),
                        )}
                        onCheckedChange={(checked) => onToggleApprover(flag, user, checked)}
                        disabled={!canToggle || flagsBusy}
                        aria-label={`${user.displayName ?? user.providerUserId} approves ${flag.title}`}
                      />
                    </TableCell>
                  ))}
                  {canManageSpend && (
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild disabled={!canToggle || spendBusy}>
                          <Button
                            variant="outline"
                            size="sm"
                            className="font-normal"
                            aria-label={`Permissions for ${user.displayName ?? user.providerUserId}`}
                          >
                            Permissions
                            <ChevronDown className="size-4 opacity-50" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuCheckboxItem
                            checked={user.viewAllSpend === true}
                            onCheckedChange={(checked) =>
                              onUserSpendAccess(user, checked === true)
                            }
                            disabled={!canToggle || spendBusy}
                          >
                            Can view all spend
                          </DropdownMenuCheckboxItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  )}
                  <TableCell className="text-right">
                    {/* Positive polarity: ON = Boo answers them. Stored field
                        stays `blocked`; presentation inverts. */}
                    <Switch
                      checked={!user.blocked}
                      onCheckedChange={(checked) => onUserBlocked(user, !checked)}
                      disabled={!canToggle || busy}
                      aria-label={`Boo answers ${user.displayName ?? user.providerUserId}`}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </>
  );
}

function PageSkeleton() {
  return (
    <>
      <Skeleton className="h-10 w-full rounded-lg" />
      <div className="flex flex-col gap-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="flex items-center justify-between gap-4 rounded-md border border-border/60 px-4 py-3"
          >
            <div className="flex flex-col gap-1.5">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-3 w-24" />
            </div>
            <Skeleton className="h-8 w-48" />
          </div>
        ))}
      </div>
    </>
  );
}

// Spend cell: just the bucket total. The raw per-service split (ai/e2b/…)
// is deliberately not surfaced in the UI — it stays in the API's `services`
// field for super admins only. A channel with no usage rows this month
// simply has no entry in the map — render a dash, not $0, so "no data" and
// "free" stay distinguishable.
function ChannelSpend({
  row,
  currency,
}: {
  row: ChannelCostRow | undefined;
  currency: string;
}) {
  if (!row) return <span className="text-muted-foreground">—</span>;
  return <span>{formatMoney(row.total, currency)}</span>;
}

function FilterGroup<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: ReadonlyArray<{ value: T; label: string }>;
}) {
  return (
    <div className="flex gap-1">
      {options.map((opt) => (
        <Button
          key={opt.value}
          variant={value === opt.value ? "secondary" : "ghost"}
          size="sm"
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </Button>
      ))}
    </div>
  );
}

// The per-channel people drawer: click a channel row → the channel's default
// (open vs allowed-people-only) plus everyone Boo knows on that platform, each
// with a "Boo answers them here" switch that writes an explicit allow/block
// override. Membership isn't tracked — people appear once they've written to
// Boo or were added on the People tab — so this is the platform roster, not a
// member list. Someone blocked everywhere shows as off and points to People.
function ChannelPeopleSheet({
  channel,
  users,
  canToggle,
  busy,
  onToggle,
  onAllowlistOnly,
  onOpenChange,
}: {
  channel: ChannelRow | null;
  users: ChannelUserRow[];
  canToggle: boolean;
  busy: boolean;
  onToggle: (user: ChannelUserRow, channel: ChannelRow, blocked: boolean) => void;
  onAllowlistOnly: (channel: ChannelRow, allowlistOnly: boolean) => void;
  onOpenChange: (open: boolean) => void;
}) {
  const [search, setSearch] = useState("");
  const channelName = channel ? (channel.displayName ?? channel.channelOrSpaceId) : "";
  const allowlistOnly = channel?.allowlistOnly === true;
  const roster = useMemo(() => {
    if (!channel) return [];
    const q = search.trim().toLowerCase();
    return users
      .filter((u) => u.platform === channel.platform)
      .filter((u) => !q || `${u.displayName ?? ""} ${u.providerUserId}`.toLowerCase().includes(q))
      .sort(
        byActivity(
          (u) => u.lastMessageAt,
          (u) => u.displayName ?? u.providerUserId,
        ),
      );
  }, [users, channel, search]);
  return (
    <Sheet open={channel !== null} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="overflow-y-auto">
        <SheetHeader className="border-b">
          <SheetTitle>{channelName}</SheetTitle>
          <SheetDescription>
            Who Boo answers in this channel. Switching someone changes it only
            here — everywhere else stays as it is.
          </SheetDescription>
        </SheetHeader>
        <div className="flex flex-col gap-3 p-4">
          <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-3">
            <div>
              <div className="text-sm font-medium">Only allowed people</div>
              <p className="text-sm text-muted-foreground">
                {allowlistOnly
                  ? "On — Boo answers only the people switched on below."
                  : "Off — Boo answers everyone here unless you switch them off."}
              </p>
            </div>
            <Switch
              checked={allowlistOnly}
              onCheckedChange={(checked) =>
                channel !== null && onAllowlistOnly(channel, checked)
              }
              disabled={!canToggle || busy}
              aria-label={`Only allowed people get answers in ${channelName}`}
            />
          </div>
          {roster.length > 8 && (
            <Input
              placeholder="Search people…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8"
            />
          )}
          {roster.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {search.trim()
                ? "No one matches."
                : "No one yet — people show up here the first time they write to Boo."}
            </p>
          ) : (
            roster.map((user) => {
              const override =
                channel !== null
                  ? user.channelOverrides?.[channel.channelOrSpaceId]
                  : undefined;
              // Explicit override wins; otherwise the channel default decides.
              const answeredHere =
                !user.blocked &&
                (override === "allow" || (override === undefined && !allowlistOnly));
              return (
                <div
                  key={`${user.platform}:${user.providerUserId}`}
                  className="flex items-center justify-between gap-3"
                >
                  <div className="flex min-w-0 items-center gap-2.5">
                    <Avatar className="size-7 shrink-0">
                      <AvatarFallback className="text-[10px]">
                        {initialsFor(user.displayName, user.providerUserId)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span
                        className={cn("truncate text-sm", user.blocked && "text-muted-foreground")}
                      >
                        {user.displayName ?? (
                          <span className="font-mono text-xs text-muted-foreground">
                            {user.providerUserId}
                          </span>
                        )}
                      </span>
                      {user.blocked && (
                        <span className="text-xs text-muted-foreground">
                          Blocked everywhere — manage on the People tab
                        </span>
                      )}
                    </div>
                  </div>
                  <Switch
                    checked={answeredHere}
                    onCheckedChange={(checked) =>
                      channel !== null && onToggle(user, channel, !checked)
                    }
                    disabled={!canToggle || busy || user.blocked}
                    aria-label={`Boo answers ${user.displayName ?? user.providerUserId} in ${channelName}`}
                  />
                </div>
              );
            })
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
